import sum from 'hash-sum';
import * as Sentry from '@sentry/nextjs';
import type { EchainPartsListItem, InteriorSeparationConfiguration } from '@hooks/states/echainPartsListSlice';
import { EchainPartsListItemOrigin } from '@hooks/states/echainPartsListSlice';
import type { EicShopSubmitPostMessageDataV1, ExchangeIaV1, ExchangeLeitungV1 } from '@igus/echain-shared-exchange';
import type {
  AssociationProductFragmentEnhanced,
  ModelAssociationFragmentEnhanced,
  ModelAssociationProductFragmentEnhanced,
  PartsListProductFragmentEnhanced,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import { AssociationType } from './associations';
import { fetchPartsListProducts } from '@libs/internal-api-service';
import { getInteriorSeparatorCountForNumGlieder } from '@libs/echain';
import { calculateTotalProductPrice, getProductBasePrice } from '@libs/product-price-utils';
import { GraphQlPriceType } from '@models/graphql';

interface ProcessedIAConfig extends InteriorSeparationConfiguration {
  productId?: string;
}

interface ProcessIaConfigEventsResult {
  processedIAConfig: ProcessedIAConfig | undefined;
  parts: EchainPartsListItem[];
}

export const groupEqualCables = (leitungen: ExchangeLeitungV1[], locale: string): Map<string, EchainPartsListItem> => {
  const groupedCableItems = new Map<string, EchainPartsListItem>();

  leitungen?.forEach((leitung: ExchangeLeitungV1) => {
    if (leitung.IsPlacedInKette && !leitung.IsPlaceholder && leitung.ArtNr?.DE) {
      // cables are handled as equal when those 5 attributes are equal
      const groupKey = sum([
        leitung.ArtNr.DE,
        leitung?.Beschriftung?.toString() || 'n.d.',
        leitung?.BeschriftungText || 'n.d.',
        leitung?.UeberstandFestpunkt?.Value.toString() || '0',
        leitung?.UeberstandMitnehmer?.Value.toString() || '0',
      ]);

      const existingCableGroup = groupedCableItems.get(groupKey);

      if (existingCableGroup) {
        existingCableGroup.quantity += 1;
        groupedCableItems.set(groupKey, existingCableGroup);
      } else {
        groupedCableItems.set(groupKey, {
          id: leitung.ArtNr?.DE || '',
          id_us: leitung.ArtNr?.US || '',
          attributes: {
            series_description: {
              label: '', // label is not used, so we don't need to set it
              value: leitung?.Bezeichnung?.[locale] ?? '',
            },
          },
          quantity: 1,
          totalPrice: -1,
          associationType: AssociationType.CABLES,
          unknownProduct: true,
          cableUeberstandFestpunkt: leitung?.UeberstandFestpunkt ?? { Value: 0, Unit: 'm' },
          cableUeberstandMitnehmer: leitung?.UeberstandMitnehmer ?? { Value: 0, Unit: 'm' },
          cableBeschriftung: leitung?.Beschriftung,
          cableBeschriftungText: leitung?.BeschriftungText,
          cableSteckerTyp: '',
          origin: EchainPartsListItemOrigin.CONFIGURATOR,
        });
      }
    }
  });

  return groupedCableItems;
};

// update ia-configurator cable data with product data from hasura
export const updateIaConfiguratorCableData = async (
  groupedCableItems: Map<string, EchainPartsListItem>,
  locale: string,
  priceCountry: string
) => {
  const uniqueCableProductIds: string[] = [];
  const cableItems = [] as EchainPartsListItem[];

  groupedCableItems.forEach(cableGroup => {
    if (!uniqueCableProductIds.includes(cableGroup.id)) {
      uniqueCableProductIds.push(cableGroup.id);
    }
  });

  if (uniqueCableProductIds.length) {
    let cables: PartsListProductFragmentEnhanced[] = [];

    try {
      cables = await fetchPartsListProducts(uniqueCableProductIds, locale, priceCountry);
    } catch (err) {
      Sentry.captureException(err);
    }

    groupedCableItems.forEach(cableGroup => {
      let cg = cableGroup;
      const matchingCable = cables.find(cable => cable.id === cableGroup.id);
      if (matchingCable) {
        cg = {
          ...cableGroup,
          attributes: matchingCable.attributes,
          prices: matchingCable.prices,
          delivery_times: matchingCable.delivery_times,
          unknownProduct: false,
        };
      }
      cableItems.push(cg);
    });
  }

  return cableItems;
};

export const processExchangeConfigurations = (
  exchangeConfigurationIAs: ExchangeIaV1[],
  ketteNumGlieder: number,
  associatedSeparatorProducts: AssociationProductFragmentEnhanced[] | null,
  associatedShelveModels: ModelAssociationFragmentEnhanced[] | undefined,
  items: EchainPartsListItem[],
  locale: string
) => {
  const partsListItems: EchainPartsListItem[] = [...items];

  exchangeConfigurationIAs?.forEach((iaItem: ExchangeIaV1) => {
    // search through our product list of associated ia products to find the item from the ia configuration
    const foundAssociatedSeparatorProduct = associatedSeparatorProducts?.find(
      associatedProduct => associatedProduct.id === iaItem.ArtNr?.DE
    );
    const foundAssociatedShelveProduct = associatedShelveModels
      ?.reduce((array: ModelAssociationProductFragmentEnhanced[], model) => array.concat(model.products), [])
      .find(associatedProduct => associatedProduct.id === iaItem.ArtNr?.DE);
    const foundAssociatedProduct = foundAssociatedSeparatorProduct || foundAssociatedShelveProduct;

    if (!iaItem.StartingAtNthKettenglied || !iaItem.PerNthKettenglied || !ketteNumGlieder) {
      throw new Error('Calculation of IA quantity not possible');
    }
    const quantity = getInteriorSeparatorCountForNumGlieder(
      iaItem.StartingAtNthKettenglied,
      iaItem.PerNthKettenglied,
      ketteNumGlieder
    );

    if (foundAssociatedProduct) {
      // when the item is found, check if it already is present in the result items collection
      // when there are multiple ia elements of the same product, it will appear as a different item in the ia configuration collection,
      // therefore we have to manually add them together
      const existingItem = partsListItems.find(item => item.id === foundAssociatedProduct.id);
      if (existingItem) {
        existingItem.quantity += quantity;

        const { totalCentPrice } = calculateTotalProductPrice(
          GraphQlPriceType.Piece,
          1,
          existingItem.quantity,
          getProductBasePrice({ prices: foundAssociatedProduct.prices } as ProductFragmentEnhanced),
          0,
          foundAssociatedProduct.prices
        );

        existingItem.totalPrice = totalCentPrice ? totalCentPrice / 100 : -1;
      } else {
        const { totalCentPrice } = calculateTotalProductPrice(
          GraphQlPriceType.Piece,
          1,
          quantity,
          getProductBasePrice({ prices: foundAssociatedProduct.prices } as ProductFragmentEnhanced),
          0,
          foundAssociatedProduct.prices
        );

        partsListItems.push({
          ...foundAssociatedProduct,
          quantity,
          totalPrice: totalCentPrice ? totalCentPrice / 100 : -1,
          associationType: foundAssociatedSeparatorProduct ? AssociationType.SEPARATORS : AssociationType.SHELVES,
          unknownProduct: false,
          origin: EchainPartsListItemOrigin.CONFIGURATOR,
        });
      }
    } else {
      // when no product is found, add it as a dummy with minimal attributes and price on demand. when dummy already exists, higher the quantity
      const existingItem = partsListItems.find(item => item.id === iaItem.ArtNr?.DE);
      if (existingItem) {
        existingItem.quantity += quantity;
      } else {
        partsListItems.push({
          id: iaItem.ArtNr?.DE || '',
          id_us: iaItem.ArtNr?.US || '',
          attributes: { series_description: iaItem.Bezeichnung?.[locale] || '' },
          quantity,
          totalPrice: -1,
          associationType: undefined,
          unknownProduct: true,
          origin: EchainPartsListItemOrigin.CONFIGURATOR,
        });
      }
    }
  });

  return partsListItems;
};

export const processIaConfigEvents = async (
  eventData: EicShopSubmitPostMessageDataV1,
  ketteNumGlieder: number,
  associatedSeparatorProducts: AssociationProductFragmentEnhanced[] | null,
  associatedShelveModels: ModelAssociationFragmentEnhanced[] | undefined,
  locale: string,
  priceCountry: string
): Promise<ProcessIaConfigEventsResult> => {
  const payloadJsonString =
    typeof eventData.payload !== 'string' ? JSON.stringify(eventData.payload) : eventData.payload;

  const groupedCableItems = groupEqualCables(eventData.payload.exchangeConfiguration.Leitungen, locale);

  const cableItems = await updateIaConfiguratorCableData(groupedCableItems, locale, priceCountry);

  const items = processExchangeConfigurations(
    eventData.payload.exchangeConfiguration.IAs,
    ketteNumGlieder,
    associatedSeparatorProducts,
    associatedShelveModels,
    cableItems,
    locale
  );

  return {
    processedIAConfig: {
      productId: eventData.payload.exchangeConfiguration.Kette?.ArtNrGenerated,
      configJson: payloadJsonString,
      configId: eventData.payload.configId,
      files: eventData.payload.files,
    },
    parts: items,
  };
};
